import React, { useState } from 'react';
import { Alert, Button, Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

// The receipt photo next to the expense form: an object URL of the file the person picked (nothing is uploaded
// twice), click or button to zoom.
export function ReceiptPreview({ url }) {
    const { t } = useTranslation();
    const [zoomed, setZoomed] = useState(false);
    return (
        <div className="receipt-preview" data-testid="receipt-preview">
            <div className={`receipt-preview-frame${zoomed ? ' is-zoomed' : ''}`}>
                <img src={url} alt={t('expenses.scan.photoAlt')} onClick={() => setZoomed((z) => !z)} />
            </div>
            <Button size="sm" variant="light" type="button" className="mt-2" onClick={() => setZoomed((z) => !z)}>
                {zoomed ? t('expenses.scan.zoomOut') : t('expenses.scan.zoomIn')}
            </Button>
        </div>
    );
}

// Warnings, duplicate notice (with a link to the existing expense), the "nothing saved yet" note and the
// collapsible recognised text. The text is shown to the logged-in person only, never stored.
export function ReceiptScanResult({ info, onReplace }) {
    const { t } = useTranslation();
    const [showText, setShowText] = useState(false);
    const warnings = (info.warnings || []).filter((w) => w !== 'possible_duplicate');
    return (
        <div className="receipt-scan-result" data-testid="receipt-scan-result">
            {info.duplicates?.length > 0 && (
                <Alert variant="warning" className="py-2 mb-2">
                    <div className="fw-semibold">{t('expenses.scan.duplicateTitle')}</div>
                    <div className="small">{t('expenses.scan.warnings.possible_duplicate')}</div>
                    {info.duplicates.map((d) => (
                        <div key={d.id} className="small">
                            <a href={`/expenses?expense=${d.id}`} target="_blank" rel="noopener noreferrer">
                                {t('expenses.scan.openExisting', { id: d.id, date: d.purchase_date })}
                            </a>
                        </div>
                    ))}
                </Alert>
            )}
            {warnings.length > 0 && (
                <Alert variant="warning" className="py-2 mb-2">
                    <div className="fw-semibold">{t('expenses.scan.warningsTitle')}</div>
                    <ul className="mb-0 ps-3 small">
                        {warnings.map((w) => <li key={w}>{t(`expenses.scan.warnings.${w}`)}</li>)}
                    </ul>
                </Alert>
            )}
            <div className="text-muted small mb-2">{t('expenses.scan.notSavedNote')}</div>
            {info.skipped?.length > 0 && (
                <div className="small mb-2" data-testid="receipt-scan-skipped">
                    {t('expenses.scan.skippedNote')}{' '}
                    <Button size="sm" variant="outline-primary" type="button" className="py-0" onClick={onReplace}>
                        {t('expenses.scan.replace')}
                    </Button>
                </div>
            )}
            {info.textLines?.length > 0 && (
                <>
                    <Button size="sm" variant="link" type="button" className="p-0" aria-expanded={showText} onClick={() => setShowText((s) => !s)}>
                        {showText ? t('expenses.scan.hideText') : t('expenses.scan.showText')}
                    </Button>
                    <Collapse in={showText}>
                        <div>
                            <pre className="receipt-scan-text small mt-2 mb-0" data-testid="receipt-scan-text">{info.textLines.join('\n')}</pre>
                        </div>
                    </Collapse>
                </>
            )}
        </div>
    );
}
